import React, { useEffect, useState } from "react";
import { getSf009AddedByDisplay } from "./sf009AddedByDisplay";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getRawSf009FormData } from "./sf009FormData";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./horizonTable.css";
import "../forms/formTitle.css";
import { addLogoAndFormHeader, applyFooterAllPages } from "../../utils/formPdfHelpers.js";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { parseSiteRegisterDate } from "./adminEditModalTheme";
import { ensureFormUuid } from "../../utils/formUuid";
import {
  buildSf009SharePointFileName,
  createSf009SharePointUploadConfig,
  getSf009SharePointFields,
  postSf009PdfToSharePoint,
} from "./sf009SharePointUpload";
import CheckTick from "../../assets/icons/Check-Tick.png";
import { drawPdfCrossIcon } from "./sf009FormSignatureUtils";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";
const THEME_RGB = [139, 22, 52];
const THEME_LIGHT = "rgb(239, 226, 231)";
const THEME_LIGHT_RGB = [239, 226, 231];
const THEME_BORDER = "rgba(139, 22, 52, 0.18)";
const API_PATH = "/api/sf-009-n-initial-site-electrical-safety-condition-report";

const QUESTIONS = [
  "Does the TTSL Authorised Person (E) have an adequate understanding of the electrical system to be worked on, it's interface points, legacy connections, the boundaries of operation and practical experience of that class of system?",
  "Does the TTSL Authorised Person (E) have a full understanding of the hazards that may arise during the course of work on the systems for which they are authorised, and the precautions that need to be taken?",
  "Does the TTSL Authorised Person (E) have contact details of the relevant Electrical Designer?",
  "Has the TTSL Authorised Person (E) had a site safety induction and become aware of all the hazards which exist on the site?",
  "Does the TTSL Authorised Person (E) have an up-to-date set of drawings for the existing electrical equipment and installation?",
  "Does the TTSL Authorised Person (E) have an up-to-date set of drawings for the new electrical equipment and installation?",
  "Does the TTSL Authorised Person (E) have access to manuals for the equipment for which he or she is authorised?",
  "Where Electrical supplies are present on site is the TTSL Authorised Person (E) aware of the location and operation of all means of isolation?",
  "Have the electrical installers been adequately briefed by the TTSL Authorised Person (E) on the current status of the Work on site and received site Inductions?",
  "Does the TTSL Authorised Person (E) have all the necessary test equipment, locks and notices available to permit the site safety systems to be correctly and safely operated in accordance with SP-009 LV Electrical Safety Rules?",
];

const DEFAULT_DATA = {
  projectName: "",
  serialNo: "",
  siteLocation: "",
  projectNo: "",
  ttslResponsiblePerson: "",
  ttslAuthorisedPersonE: "",
  apStartOnSiteDate: "",
  projectCompletionDate: "",
  printName: "",
  printOffice: "",
  declarationRows: QUESTIONS.map((q, i) => ({ itemNo: i + 1, question: q, answer: "" })),
  actionItems: Array.from({ length: 10 }, (_, i) => ({ itemNo: i + 1, action: "" })),
  existingHazardsControlMeasures: "",
  newWorksHazardsControlMeasures: "",
  enablingWorksAndInterfaces: "",
  otherRelevantInformation: "",
  finalName: "",
  finalSigned: "",
  finalDate: "",
};

const base = () => {
  const url = process.env.REACT_APP_API_URL;
  return url?.endsWith("/") ? url.slice(0, -1) : url || "";
};

const parseDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const d = parseSiteRegisterDate(v);
  if (d) return d;
  const date = new Date(v);
  return isNaN(date.getTime()) ? null : date;
};
const ymd = (d) => (d && !isNaN(d.getTime()) ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "");
const norm = (v) => {
  const s = String(v || "").toLowerCase();
  if (["yes", "y", "true", "1"].includes(s)) return "Yes";
  if (["no", "n", "false", "0"].includes(s)) return "No";
  return "";
};

const getSignatureSrc = (v) => {
  const s = String(v || "").trim();
  if (!s) return "";
  if (s.startsWith("data:image/")) return s;
  if (/^[A-Za-z0-9+/=\s]+$/.test(s) && s.replace(/\s+/g, "").length > 80) return `data:image/png;base64,${s.replace(/\s+/g, "")}`;
  return "";
};

function mergeData(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  return {
    ...DEFAULT_DATA,
    ...r,
    declarationRows: Array.isArray(r.declarationRows)
      ? r.declarationRows.map((x, i) => ({ itemNo: Number(x.itemNo) || i + 1, question: String(x.question || QUESTIONS[i] || ""), answer: norm(x.answer) }))
      : DEFAULT_DATA.declarationRows.map((x) => ({ ...x })),
    actionItems: Array.isArray(r.actionItems)
      ? r.actionItems.map((x, i) => ({ itemNo: Number(x.itemNo) || i + 1, action: String(x.action || "") }))
      : DEFAULT_DATA.actionItems.map((x) => ({ ...x })),
  };
}

export default function Sf009NInitialSiteElectricalSafetyConditionReportPage() {
  const canEditDelete = canEditDeleteRecords();
  const isAdminUser = adminIsPrimaryAdmin();
  const [forms, setForms] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [uploadingKey, setUploadingKey] = useState(null);

  useEffect(() => {
    fetchForms();
    fetchUsers();
  }, []);

  const rawOf = getRawSf009FormData;

  const fetchForms = async () => {
    setLoadingRecords(true);
    try {
      const res = await fetch(base() + API_PATH, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      const json = await res.json();
      setForms(res.ok && json.success && Array.isArray(json.data) ? json.data : []);
    } catch {
      setForms([]);
    } finally {
      setLoadingRecords(false);
    }
  };
  const fetchUsers = async () => {
    try {
      const res = await fetch(base() + "/api/app-users", { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      const json = await res.json();
      setUsers(res.ok && json.success && Array.isArray(json.data) ? json.data : []);
    } catch {
      setUsers([]);
    }
  };
  const userName = (row) => getSf009AddedByDisplay(row, users);
  const fmt = (v) => {
    const d = parseDate(v);
    return d ? d.toLocaleDateString("en-GB") : "—";
  };

  const filtered = forms.filter((f) => {
    const d = mergeData(rawOf(f));
    const str = `${d.projectName} ${d.siteLocation} ${d.ttslAuthorisedPersonE}`.toLowerCase();
    return search.trim().toLowerCase().split(/\s+/).filter(Boolean).every((w) => str.includes(w));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const update = (k, v) => setEditForm((p) => ({ ...p, formData: { ...p.formData, [k]: v } }));
  const updateDecl = (i, v) =>
    setEditForm((p) => {
      const rows = [...(p.formData.declarationRows || [])];
      rows[i] = { ...rows[i], answer: v };
      return { ...p, formData: { ...p.formData, declarationRows: rows } };
    });
  const updateAction = (i, v) =>
    setEditForm((p) => {
      const rows = [...(p.formData.actionItems || [])];
      rows[i] = { ...rows[i], action: v };
      return { ...p, formData: { ...p.formData, actionItems: rows } };
    });
  const handleEdit = (f) => {
    setEditForm({
      ...f,
      formUuid: ensureFormUuid(f?.formUuid),
      formData: mergeData(rawOf(f)),
    });
    setShowEditModal(true);
  };

  const saveEdit = async () => {
    if (!editForm?._id) return;
    try {
      const raw = mergeData(editForm.formData);
      if (raw.finalSigned && !getSignatureSrc(raw.finalSigned)) {
        toast.error("Signed must be a valid signature image.");
        return;
      }
      const body = { ...raw, formUuid: ensureFormUuid(editForm.formUuid) };
      const res = await fetch(base() + API_PATH + "/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        toast.success("Form updated!");
        setShowEditModal(false);
        setEditForm(null);
        fetchForms();
      } else toast.error(json.message || "Failed to update");
    } catch {
      toast.error("Failed to update");
    }
  };

  const confirmDelete = async () => {
    try {
      const res = await fetch(base() + API_PATH + "/" + deleteId, { method: "DELETE", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      const json = await res.json();
      if (res.ok && json.success) {
        toast.success("Form deleted");
        fetchForms();
      } else toast.error(json.message || "Failed to delete");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const generatePDF = async (row, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? createSf009SharePointUploadConfig(options) : null;
    const raw = mergeData(rawOf(row));
    const doc = new jsPDF({ orientation: "landscape" });
    const w = doc.internal.pageSize.getWidth();
    const margin = { left: 10, right: 10 };
    const cw = w - 20;
    const lbl = { fillColor: THEME_LIGHT_RGB, textColor: THEME_RGB, fontStyle: "bold" };
    const val = { fillColor: [255, 255, 255] };
    const grid = { font: "helvetica", fontSize: 7.5, lineWidth: 0.2, cellPadding: 2.2, valign: "middle" };
    const finalSignatureSrc = getSignatureSrc(raw.finalSigned);
    let y = await addLogoAndFormHeader(doc, "Initial Site Electrical Safety Condition Report");

    autoTable(doc, { startY: y, theme: "grid", tableWidth: cw, margin, styles: grid, body: [
      ["Project name", raw.projectName, "Serial no", raw.serialNo],
      ["Site / location:", raw.siteLocation, "Project no", raw.projectNo],
      [{ content: "Project Details", colSpan: 4, styles: { ...lbl, halign: "center" } }],
      ["TTSL Responsible Person", raw.ttslResponsiblePerson, "TTSL Authorised Person (E)", raw.ttslAuthorisedPersonE],
      ["AP Start on site date", fmt(raw.apStartOnSiteDate), "Project Completion Date", fmt(raw.projectCompletionDate)],
    ], columnStyles: { 0: { cellWidth: cw * 0.22, ...lbl }, 1: { cellWidth: cw * 0.28, ...val }, 2: { cellWidth: cw * 0.22, ...lbl }, 3: { cellWidth: cw * 0.28, ...val } } });
    y = doc.lastAutoTable.finalY;

    const declarationBody = [];
    const questionRowByBodyRowIndex = [];
    raw.declarationRows.forEach((r, idx) => {
      declarationBody.push([`${r.itemNo}. ${r.question}`, r.answer === "Yes" ? " " : "", r.answer === "No" ? " " : ""]);
      questionRowByBodyRowIndex.push(idx);
      if (idx === 2) {
        declarationBody.push([
          { content: `Print Name: ${raw.printName || ""}`, styles: lbl },
          { content: "Print Office:", styles: lbl },
          raw.printOffice || "",
        ]);
        questionRowByBodyRowIndex.push(null);
      }
    });

    autoTable(doc, {
      startY: y, theme: "grid", tableWidth: cw, margin, styles: { ...grid, minCellHeight: 8.5 },
      head: [[{ content: "Documentation Declaration", styles: lbl }, { content: "Yes", styles: { ...lbl, halign: "center" } }, { content: "No", styles: { ...lbl, halign: "center" } }]],
      body: declarationBody,
      columnStyles: { 0: { cellWidth: cw * 0.74 }, 1: { cellWidth: cw * 0.13, halign: "center" }, 2: { cellWidth: cw * 0.13, halign: "center" } },
      didDrawCell: (d) => {
        if (d.section !== "body") return;
        const questionIndex = questionRowByBodyRowIndex[d.row.index];
        if (questionIndex == null) return;
        const rowData = raw.declarationRows[questionIndex];
        const s = 3.2;
        const x = d.cell.x + d.cell.width / 2 - s / 2;
        const yPos = d.cell.y + d.cell.height / 2 - s / 2;
        if (d.column.index === 1 && rowData?.answer === "Yes") { try { doc.addImage(CheckTick, "PNG", x, yPos, s, s); } catch {} }
        if (d.column.index === 2 && rowData?.answer === "No") drawPdfCrossIcon(doc, x, yPos, s);
      },
    });
    y = doc.lastAutoTable.finalY;

    autoTable(doc, { startY: y, theme: "grid", tableWidth: cw, margin, styles: { ...grid, minCellHeight: 8.5 }, body: [
      [{ content: "Actions on items 1 to 10 above", styles: lbl }, { content: "Add more rows if needed  insert the relevant item", styles: lbl }],
      ...raw.actionItems.map((r) => [`${r.itemNo}.`, r.action]),
      [{ content: "Note:  It may not be possible to answer 'Yes' to some of the above questions. If this is the case, comments must be made under 'comments' to state what Action has been done to ensure safe working may be carried out.", colSpan: 2, styles: { ...lbl, fontStyle: "normal" } }],
    ], columnStyles: { 0: { cellWidth: cw * 0.38 }, 1: { cellWidth: cw * 0.62 } } });

    doc.addPage();
    autoTable(doc, { startY: 12, theme: "grid", tableWidth: cw, margin, styles: { ...grid, minCellHeight: 9 }, body: [
      [{ content: "Relevant information and details", colSpan: 2, styles: { ...lbl, halign: "center" } }],
      [{ content: "Existing Hazards and Control Measures", colSpan: 2, styles: lbl }],
      [{ content: raw.existingHazardsControlMeasures, colSpan: 2, styles: { minCellHeight: 34, valign: "top", ...val } }],
      [{ content: "New Works Hazards and control Measures", colSpan: 2, styles: lbl }],
      [{ content: raw.newWorksHazardsControlMeasures, colSpan: 2, styles: { minCellHeight: 34, valign: "top", ...val } }],
      [{ content: "Enabling Works and Interfaces", colSpan: 2, styles: lbl }],
      [{ content: raw.enablingWorksAndInterfaces, colSpan: 2, styles: { minCellHeight: 30, valign: "top", ...val } }],
      [{ content: "Other relevant information", colSpan: 2, styles: lbl }],
      [{ content: raw.otherRelevantInformation, colSpan: 2, styles: { minCellHeight: 30, valign: "top", ...val } }],
      [{ content: "I confirm that, as an TTSL Authorised Person (E), I have sufficient documentation and understanding of the site electrical systems to enable me to implement the SF-009 LV Electrical Safety Procedures safely and correctly.", colSpan: 2, styles: lbl }],
    ] });
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY,
      theme: "grid",
      tableWidth: cw,
      margin,
      styles: grid,
      body: [["Name:", raw.finalName, "Signed:", " ", "Date:", fmt(raw.finalDate)]],
      columnStyles: {
        0: { cellWidth: cw * 0.1, ...lbl },
        1: { cellWidth: cw * 0.3, ...val },
        2: { cellWidth: cw * 0.12, ...lbl },
        3: { cellWidth: cw * 0.28, minCellHeight: 16, ...val },
        4: { cellWidth: cw * 0.08, ...lbl },
        5: { cellWidth: cw * 0.12, ...val },
      },
      didDrawCell: (data) => {
        if (!finalSignatureSrc) return;
        if (data.section === "body" && data.row.index === 0 && data.column.index === 3) {
          try {
            doc.addImage(finalSignatureSrc, "PNG", data.cell.x + 2, data.cell.y + 2, Math.min(34, data.cell.width - 4), Math.min(12, data.cell.height - 4), undefined, "NONE");
          } catch {}
        }
      },
    });
    applyFooterAllPages(doc);
    const localName = `SF009N_${String(raw.ttslAuthorisedPersonE || "Report").replace(/[\\/:"*?<>|]+/g, "-")}.pdf`;
    const fileName =
      shouldUploadToSharePoint && uploadConfig
        ? buildSf009SharePointFileName("N", getSf009SharePointFields("N", raw, row, userName))
        : localName;
    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${row._id || ""}|${raw.projectName || ""}|${raw.siteLocation || ""}`;
        await postSf009PdfToSharePoint({ doc, fileName, uploadConfig, setUploadingKey, uploadKey: key });
        toast.success("PDF uploaded to SharePoint successfully!");
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
      }
    } else {
      doc.save(fileName);
      toast.success("SF-009-N PDF generated successfully!");
    }
  };

  const fd = editForm?.formData ? mergeData(editForm.formData) : DEFAULT_DATA;
  const sectionHead = {
    borderBottom: `1px solid ${THEME_BORDER}`,
    background: "#fff",
    color: "#1a202c",
    fontWeight: 700,
    fontSize: 15,
    textAlign: "center",
    padding: "14px 12px",
    lineHeight: 1.2,
  };
  const lblCell = {
    border: `1px solid ${THEME_BORDER}`,
    background: THEME_LIGHT,
    color: THEME,
    fontWeight: 700,
    fontSize: 16,
    padding: "6px 8px",
    verticalAlign: "middle",
  };
  const valCell = {
    border: `1px solid ${THEME_BORDER}`,
    background: "#fff",
    padding: "4px 8px",
    verticalAlign: "middle",
  };
  const cellInput = {
    width: "100%",
    border: "none",
    outline: "none",
    fontSize: 14,
    background: "transparent",
  };
  const yesNoCell = {
    border: `1px solid ${THEME_BORDER}`,
    background: "#fff",
    textAlign: "center",
    verticalAlign: "middle",
    width: "8%",
  };
  const bigText = {
    width: "100%",
    border: "none",
    outline: "none",
    resize: "none",
    minHeight: 118,
    fontSize: 14,
    background: "transparent",
  };

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-009-N Initial Site Electrical Safety Condition Report</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: THEME,
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 250, boxSizing: "border-box" }}
              />
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Project Name</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Site / Location</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Authorised Person (E)</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Start Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />
                    Loading records...
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>
                    No records found.
                  </td>
                </tr>
              ) : (
                paged.map((f) => {
                  const d = mergeData(rawOf(f));
                  return (
                    <tr key={f._id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{d.projectName || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{d.siteLocation || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{d.ttslAuthorisedPersonE || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{fmt(d.apStartOnSiteDate)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{userName(f)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{fmt(f.updatedAt || f.createdAt)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button onClick={() => handleEdit(f)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                            </button>
                          )}
                          <button type="button" onClick={() => generatePDF(f)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                            <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} />
                          </button>
                          {(() => {
                            const k = `${f._id || ""}|${d.projectName || ""}|${d.siteLocation || ""}`;
                            const busy = uploadingKey === k;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!busy) generatePDF(f, { uploadOnly: true, onSuccess: () => {}, onError: () => {} });
                                }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{ background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer", padding: 6, opacity: busy ? 0.6 : 1 }}
                              >
                                <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                              </button>
                            );
                          })()}
                          {canEditDelete && (
                            <button onClick={() => { setDeleteId(f._id); setShowDeleteModal(true); }} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button className="horizon-table-btn" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === 1 ? "#f7fafc" : "#fff", color: page === 1 ? "#a0aec0" : THEME, cursor: page === 1 ? "not-allowed" : "pointer" }}>
            Prev
          </button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>
            {page} / {totalPages}
          </span>
          <button className="horizon-table-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === totalPages ? "#f7fafc" : "#fff", color: page === totalPages ? "#a0aec0" : THEME, cursor: page === totalPages ? "not-allowed" : "pointer" }}>
            Next
          </button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(26, 32, 44, 0.45)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 1180, margin: "0 auto", border: "none", borderRadius: 16, overflow: "hidden", boxShadow: "0 12px 40px rgba(139, 22, 52, 0.15)" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff", padding: "18px 24px" }}>
                <h5 className="modal-title" style={{ fontWeight: 700, fontSize: 17, lineHeight: 1.35 }}>Edit SF-009-N Initial Site Electrical Safety Condition Report</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} aria-label="Close" />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", padding: "20px 24px 8px", background: "#fff" }}>
                <div style={{ border: `1px solid ${THEME_BORDER}`, background: "#fff" }}>
                  <div style={sectionHead}>Initial Site Electrical Safety Condition Report</div>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <tbody>
                      <tr>
                        <td style={{ ...lblCell, width: "21%" }}>Project name</td>
                        <td style={{ ...valCell, width: "29%" }}>
                          <input value={fd.projectName} onChange={(e) => update("projectName", e.target.value)} style={cellInput} />
                        </td>
                        <td style={{ ...lblCell, width: "11%" }}>Serial no</td>
                        <td style={{ ...valCell, width: "14%" }}>
                          <input value={fd.serialNo} onChange={(e) => update("serialNo", e.target.value)} style={cellInput} />
                        </td>
                        <td style={{ ...valCell, width: "25%" }} />
                      </tr>
                      <tr>
                        <td style={lblCell}>Site / location:</td>
                        <td style={valCell}>
                          <input value={fd.siteLocation} onChange={(e) => update("siteLocation", e.target.value)} style={cellInput} />
                        </td>
                        <td style={lblCell}>Project no</td>
                        <td style={valCell}>
                          <input value={fd.projectNo} onChange={(e) => update("projectNo", e.target.value)} style={cellInput} />
                        </td>
                        <td style={valCell} />
                      </tr>
                      <tr>
                        <td colSpan={5} style={{ ...lblCell, textAlign: "center", fontSize: 15, padding: "14px 12px" }}>Project Details</td>
                      </tr>
                      <tr>
                        <td style={lblCell}>TTSL Responsible Person</td>
                        <td style={valCell}>
                          <input value={fd.ttslResponsiblePerson} onChange={(e) => update("ttslResponsiblePerson", e.target.value)} style={cellInput} />
                        </td>
                        <td style={lblCell}>TTSL Authorised Person (E)</td>
                        <td style={valCell}>
                          <input value={fd.ttslAuthorisedPersonE} onChange={(e) => update("ttslAuthorisedPersonE", e.target.value)} style={cellInput} />
                        </td>
                        <td style={valCell} />
                      </tr>
                      <tr>
                        <td style={lblCell}>AP Start on site date</td>
                        <td style={valCell}>
                          <DatePicker selected={parseDate(fd.apStartOnSiteDate)} onChange={(d) => update("apStartOnSiteDate", ymd(d))} dateFormat="dd/MM/yyyy" className="form-control form-control-sm border-0 p-0" />
                        </td>
                        <td style={lblCell}>Project Completion Date</td>
                        <td style={valCell}>
                          <DatePicker selected={parseDate(fd.projectCompletionDate)} onChange={(d) => update("projectCompletionDate", ymd(d))} dateFormat="dd/MM/yyyy" className="form-control form-control-sm border-0 p-0" />
                        </td>
                        <td style={valCell} />
                      </tr>
                    </tbody>
                  </table>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <thead>
                      <tr>
                        <th style={{ ...lblCell, textAlign: "left", width: "84%" }}>Documentation Declaration</th>
                        <th style={{ ...lblCell, textAlign: "center", width: "8%" }}>Yes</th>
                        <th style={{ ...lblCell, textAlign: "center", width: "8%" }}>No</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fd.declarationRows.map((row, i) => (
                        <React.Fragment key={i}>
                          <tr>
                            <td style={{ ...valCell, fontSize: 14 }}>
                              <span style={{ display: "inline-block", minWidth: 22 }}>{row.itemNo}.</span>
                              {row.question}
                            </td>
                            <td style={yesNoCell}>
                              <input
                                type="radio"
                                name={`q_${i}`}
                                checked={row.answer === "Yes"}
                                onChange={() => updateDecl(i, "Yes")}
                                style={{ accentColor: THEME, cursor: "pointer" }}
                              />
                            </td>
                            <td style={yesNoCell}>
                              <input
                                type="radio"
                                name={`q_${i}`}
                                checked={row.answer === "No"}
                                onChange={() => updateDecl(i, "No")}
                                style={{ accentColor: THEME, cursor: "pointer" }}
                              />
                            </td>
                          </tr>
                          {i === 2 && (
                            <tr>
                              <td style={{ ...valCell, padding: 0 }} colSpan={3}>
                                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                                  <tbody>
                                    <tr>
                                      <td style={{ ...lblCell, width: "16%" }}>Print Name:</td>
                                      <td style={{ ...valCell, width: "34%" }}>
                                        <input value={fd.printName} onChange={(e) => update("printName", e.target.value)} style={cellInput} />
                                      </td>
                                      <td style={{ ...lblCell, width: "16%" }}>Print Office:</td>
                                      <td style={{ ...valCell, width: "34%" }}>
                                        <input value={fd.printOffice} onChange={(e) => update("printOffice", e.target.value)} style={cellInput} />
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <tbody>
                      <tr>
                        <td style={{ ...lblCell, width: "43%" }}>Actions on items 1 to 10 above</td>
                        <td style={{ ...lblCell, width: "57%", textAlign: "center" }}>Add more rows if needed&nbsp;&nbsp; insert the relevant item</td>
                      </tr>
                      {fd.actionItems.map((row, i) => (
                        <tr key={i}>
                          <td style={valCell}>
                            {row.itemNo}.
                          </td>
                          <td style={valCell}>
                            <input value={row.action} onChange={(e) => updateAction(i, e.target.value)} style={cellInput} />
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={2} style={{ ...lblCell, fontWeight: 600 }}>
                          Note:&nbsp; It may not be possible to answer 'Yes' to some of the above questions. If this is the case, entries must be made under 'comments' to state what Action has been done to ensure safe working may be carried out.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ border: `1px solid ${THEME_BORDER}`, borderTop: "none", background: "#fff", marginTop: 12 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <tbody>
                      <tr>
                        <td colSpan={2} style={{ ...lblCell, textAlign: "center", fontSize: 15, padding: "14px 12px" }}>Relevant information and details</td>
                      </tr>
                      <tr>
                        <td colSpan={2} style={lblCell}>Existing Hazards and Control Measures</td>
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ ...valCell, height: 120 }}>
                          <textarea value={fd.existingHazardsControlMeasures} onChange={(e) => update("existingHazardsControlMeasures", e.target.value)} style={bigText} />
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={2} style={lblCell}>New Works Hazards and control Measures</td>
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ ...valCell, height: 120 }}>
                          <textarea value={fd.newWorksHazardsControlMeasures} onChange={(e) => update("newWorksHazardsControlMeasures", e.target.value)} style={bigText} />
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={2} style={lblCell}>Enabling Works and Interfaces</td>
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ ...valCell, height: 120 }}>
                          <textarea value={fd.enablingWorksAndInterfaces} onChange={(e) => update("enablingWorksAndInterfaces", e.target.value)} style={bigText} />
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={2} style={lblCell}>Other relevant information</td>
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ ...valCell, height: 120 }}>
                          <textarea value={fd.otherRelevantInformation} onChange={(e) => update("otherRelevantInformation", e.target.value)} style={bigText} />
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ ...lblCell, fontWeight: 700 }}>
                          I confirm that, as an TTSL Authorised Person (E), I have sufficient documentation and understanding of the site electrical systems to enable me to implement the SF-009 LV Electrical Safety Procedures safely and correctly.
                        </td>
                      </tr>
                      <tr>
                        <td style={{ ...lblCell, width: "15%" }}>Name:</td>
                        <td style={valCell}>
                          <input value={fd.finalName} onChange={(e) => update("finalName", e.target.value)} style={cellInput} />
                        </td>
                      </tr>
                      <tr>
                        <td style={lblCell}>Signed:</td>
                        <td style={valCell}>
                          <div style={{ minHeight: 44, display: "flex", alignItems: "center", justifyContent: "flex-start", background: "#fff" }}>
                            {getSignatureSrc(fd.finalSigned) ? (
                              <img src={getSignatureSrc(fd.finalSigned)} alt="Signature" style={{ maxHeight: 38, maxWidth: "100%", objectFit: "contain" }} />
                            ) : (
                              <span style={{ color: "#9ca3af", fontSize: 12 }}>No signature available</span>
                            )}
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td style={lblCell}>Date:</td>
                        <td style={valCell}>
                          <DatePicker selected={parseDate(fd.finalDate)} onChange={(d) => update("finalDate", ymd(d))} dateFormat="dd/MM/yyyy" className="form-control form-control-sm border-0 p-0" />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer" style={{ background: "#fff", borderTop: `1px solid ${THEME_BORDER}`, padding: "14px 24px", gap: 10 }}>
                <button type="button" className="btn btn-light" style={{ borderRadius: 8, fontWeight: 600, border: `1px solid ${THEME_BORDER}` }} onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600, borderRadius: 8, padding: "8px 22px", border: "none" }} onClick={saveEdit}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-009-N Initial Site Electrical Safety Condition Report</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this form?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-009-N Initial Site Electrical Safety Condition Report File"
        importPath="sf-009-n-initial-site-electrical-safety-condition-report/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setForms((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />
    </>
  );
}
